import {
  Action,
  Alert,
  Badge,
  Card,
  Combobox,
  ComboboxOption,
  DateInput,
  Fieldset,
  Icon,
  Label,
  MultiSelect,
  NumberInput,
  Page,
  RadioGroupInput,
  SimpleSelect,
  Skeleton,
  SwitchInput,
  TextAreaInput,
  TextInput,
} from 'corso-admin-ui';
import {
  automationsDisplayName,
  CrewClaimResolutionMethodEnum,
  crewClaimResolutionMethodEnumName,
  CrewIncentiveTypeEnum,
  CrewMerchantUi,
  eventTypeDescription,
  isTruthy,
  Pegasus,
  toEnum,
} from 'corso-types';
import {
  ComponentProps,
  FC,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
} from 'react';
import { Controller, FormProvider, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { formatter } from 'corso-web-lib';
import MonetaryRateInput, {
  MonetaryType,
} from '~/components/field/MonetaryRateInput';
import {
  useAutomations,
  useAutomationUpsert,
  useNextPriorityByHook,
} from '~/features/settings/hooks/useAutomations';
import { useCustomFields } from '~/features/settings/hooks/useCustomFields';
import { useFactValues } from '~/features/settings/hooks/useFactValues';
import { useProductGroups } from '~/features/settings/hooks/useProductGroups';
import { useHasCheckoutFunctionality } from '~/features/settings/hooks/useShopifyPlan';
import { useStoreClaimTags } from '~/hooks/useClaimTags';
import { useCurrency } from '~/hooks/useCurrency';
import { usePathParams } from '~/hooks/usePathParams';
import { useSearchParam } from '~/hooks/useSearchParam';
import { useStoreId } from '~/providers/StoreUserProvider';
import { Automation, AutomationCreate, AutomationUpdate } from '~/types';
import { setMethods } from '~/utils/setMethods';
import EventApplyFee from './eventParams/EventApplyFee';
import EventApplyLineItemFee from './eventParams/EventApplyLineItemFee';
import EventOfferAdvancedExchanges from './eventParams/EventOfferAdvancedExchange';
import EventOrderEditing from './eventParams/EventOrderEditing';
import EventQuoteOrderShipmentDeliveryEstimate from './eventParams/EventQuoteOrderShipmentDeliveryEstimate';
import EventQuoteOrderShipmentModify from './eventParams/EventQuoteOrderShipmentModify';
import useIsIntelligemsEnabled from './hooks/useIsIntelligemsEnabled';

import {
  AutomationTemplate,
  automationTemplatesWithIds,
} from './AutomationTemplates';
import { EventCollectCustomFields } from './eventParams/EventCollectCustomFields';
import EventCreateGorgiasTicket from './eventParams/EventCreateGorgiasTicket';
import { EventCustomizeReturnShipment } from './eventParams/EventCustomizeReturnShipment';
import EventMerchantPaidProtection from './eventParams/EventMerchantPaidProtection';
import EventModifyResolutionWindow from './eventParams/EventModifyResolutionWindow';
import EventAutoFinalizeClaimSettings from './eventParams/EventOutOfStock';
import EventQuoteOrderShipment from './eventParams/EventQuoteOrderShipment';
import EventQuoteOrderShipmentParcel from './eventParams/EventQuoteOrderShipmentParcel';
import EventRegistrationDiscount from './eventParams/EventRegistrationDiscount';
import EventRestockInventory from './eventParams/EventRestockInventory';
import EventReturnShippingConfig from './eventParams/EventReturnShippingConfig';
import EventRightToWithdraw from './eventParams/EventRightToWithdraw';
import { EventSetOrderShippingTitle } from './eventParams/EventSetOrderShippingTitle';
import { EventShippingClaimAllowedResolutions } from './eventParams/EventShippingClaimAllowedResolutions';
import { EventShippingClaimApplyOrderTags } from './eventParams/EventShippingClaimApplyOrderTags';
import { EventShippingClaimReorderLocations } from './eventParams/EventShippingClaimReorderLocations';
import { EventShippingClaimReorderShipping } from './eventParams/EventShippingClaimReorderShipping';
import { EventShippingClaimSlaAlert } from './eventParams/EventShippingClaimSlaAlert';
import EventShopNow from './eventParams/EventShopNow';
import {
  AutomationFormValues,
  useAutomationForm,
  useAutomationFormContext,
} from './hooks/useAutomationForm';
import { useEventDefaultParams } from './hooks/useEventDefaultParams';

// ? maybe merge with `displayName`
const valueTypeOperator: Partial<
  Record<Pegasus.ValueType, Partial<Record<Pegasus.Operator, string>>>
> = {
  [Pegasus.ValueType.date]: {
    [Pegasus.Operator.lessThan]: 'Before',
    [Pegasus.Operator.greaterThan]: 'After',
  },
} satisfies {
  [V in Pegasus.ValueType]?: Record<
    (typeof Pegasus.valueOperators)[V][number],
    string
  >;
};

// ? maybe merge with `displayName`
const matchCondition = {
  all: 'Matches all of the following',
  any: 'Matches any of the following',
} as const;

const booleanExpressionBadge = {
  all: <Badge variant="info">AND</Badge>,
  any: <Badge variant="info">OR</Badge>,
} as const; // ? maybe extend to include `NOT`, or possibly merge with `matchCondition`

/** Supporting type for definite value type inputs, as using the underlying content within a generic didn't work as expected. */
type DefiniteData<T extends Pegasus.DefiniteValueType> = {
  [Pegasus.ValueType
    .numberArray]: CrewMerchantUi.ArrayFactData['definite'][Pegasus.ArrayFactByValueType<
    Pegasus.ValueType.numberArray,
    Pegasus.ArrayFactType.definite
  >];
  [Pegasus.ValueType
    .stringArray]: CrewMerchantUi.ArrayFactData['definite'][Pegasus.ArrayFactByValueType<
    Pegasus.ValueType.stringArray,
    Pegasus.ArrayFactType.definite
  >];
}[T];

/** Supporting type for arbitrary value type inputs, as using the underlying content within a generic didn't work as expected. */
type ArbitraryData<T extends Pegasus.ArbitraryValueType> = {
  [Pegasus.ValueType
    .stringArray]: CrewMerchantUi.ArrayFactData['arbitrary'][Pegasus.ArrayFactByValueType<
    Pegasus.ValueType.stringArray,
    Pegasus.ArrayFactType.arbitrary
  >];
}[T];

const arbitraryValueTypeFormatter: {
  [V in Pegasus.ArbitraryValueType]: (
    value: Pegasus.ValueTypeData[V],
  ) => ReactNode;
} = {
  [Pegasus.ValueType.stringArray]: ([...values]) =>
    !values.length ? (
      <Badge variant="warning">None</Badge>
    ) : (
      <Badge>
        <strong className="font-medium">
          {values.sort((a, b) => a.localeCompare(b)).join(', ')}
        </strong>
      </Badge>
    ),
};

/** If values exceeds this amount, a fallback is displayed.
 * This might need to be dependent on the fact; i.e. country vs tags
 */
const MAX_ARRAY_VALUES_VISIBLE = 3;
const definiteValueTypeFormatter: {
  [V in Pegasus.DefiniteValueType]: (
    values: Pegasus.ValueTypeData[V],
    options: DefiniteData<V>,
  ) => ReactNode;
} = {
  [Pegasus.ValueType.stringArray]: ([...values], [...options]) => {
    const flatOptions = options.flatMap((option) =>
      'options' in option ? option.options : [option],
    );
    const labels = values
      .map((value) => flatOptions.find((option) => option.value === value))
      .filter(isTruthy) // ? maybe have a fallback for values not found
      .map((option) => option.label)
      .sort((a, b) => a.localeCompare(b));

    if (!values.length) return <Badge variant="warning">None</Badge>;

    return (
      <Badge>
        {labels.length > MAX_ARRAY_VALUES_VISIBLE ? (
          <>
            <strong className="font-medium">
              {labels.slice(0, MAX_ARRAY_VALUES_VISIBLE).join(', ')}
            </strong>
            {', '}
            and {labels.length - MAX_ARRAY_VALUES_VISIBLE} More
          </>
        ) : (
          <strong className="font-medium">{labels.join(', ')}</strong>
        )}
      </Badge>
    );
  },
  [Pegasus.ValueType.numberArray]: ([...values], [...options]) => {
    const flatOptions = options.flatMap((option) =>
      'options' in option ? option.options : [option],
    );
    const labels = values
      .map((value) => flatOptions.find((option) => option.value === value))
      .filter(isTruthy) // ? maybe have a fallback for values not found
      .map((option) => option.label)
      .sort((a, b) => a.localeCompare(b));

    if (!values.length) return <Badge variant="info">None</Badge>;

    return (
      <Badge>
        {labels.length > MAX_ARRAY_VALUES_VISIBLE ? (
          <>
            <strong className="font-medium">
              {labels.slice(0, MAX_ARRAY_VALUES_VISIBLE).join(', ')}
            </strong>
            {', '}
            and {labels.length - MAX_ARRAY_VALUES_VISIBLE} More
          </>
        ) : (
          <strong className="font-medium">{labels.join(', ')}</strong>
        )}
      </Badge>
    );
  },
};

const singleValueTypeFormatter: {
  [V in Exclude<Pegasus.ValueType, Pegasus.ArrayValueType>]: (
    value: Pegasus.ValueTypeData[V],
  ) => ReactNode;
} = {
  // ? maybe need a separate currency value type
  [Pegasus.ValueType.number]: (value) => (
    <Badge>
      <strong className="font-medium">{value}</strong>
    </Badge>
  ),
  [Pegasus.ValueType.boolean]: (value) => (
    <Badge>
      <strong className="font-medium">{`${value}`}</strong>
    </Badge>
  ),
  // date string with no time component
  [Pegasus.ValueType.date]: (value) => (
    <Badge>
      <strong className="font-medium">{formatter.date(value)}</strong>
    </Badge>
  ),
};

const valueTypeDefault: {
  [V in Pegasus.ValueType]: () => Pegasus.ValueTypeData[V];
} = {
  [Pegasus.ValueType.numberArray]: () => [],
  [Pegasus.ValueType.stringArray]: () => [],
  [Pegasus.ValueType.number]: () => 0, // could cause issues if used for ID selection; however, that should be a definite lookup instead
  [Pegasus.ValueType.boolean]: () => false,
  [Pegasus.ValueType.date]: () => new Date().toISOString(),
} as const satisfies {
  [V in Pegasus.ValueType]: () => Pegasus.ValueTypeData[V];
};

type ValueTypeInputProps<V extends Pegasus.ValueType> = {
  id: string;
  error?: string;
  fact: Pegasus.Fact;
  value: Pegasus.ValueTypeData[V];
  onChange: (value: Pegasus.ValueTypeData[V]) => void;
};

const arbitraryValueTypeInput: {
  [V in Pegasus.ArbitraryValueType]: FC<
    ValueTypeInputProps<V> & {
      options: ArbitraryData<V>;
    }
  >;
} = {
  [Pegasus.ValueType.stringArray]: ({
    fact,
    value,
    onChange,
    options,
    error,
  }) => (
    <div className="grow">
      <MultiSelect
        label={`${automationsDisplayName.fact[fact]} Value`}
        placeholder={`Find or Add ${automationsDisplayName.fact[fact]}`}
        labelVisuallyHidden
        options={options.map((option) => ({
          label: option,
          searchValue: option,
          value: option,
        }))}
        value={value.map((option) => ({
          label: option,
          searchValue: option,
          value: option,
        }))}
        onChange={(selections) =>
          onChange(selections.map((selection) => selection.value))
        }
        error={error}
        creatable
      />
    </div>
  ),
};

const definiteValueTypeInput: {
  [V in Pegasus.DefiniteValueType]: FC<
    ValueTypeInputProps<V> & {
      options: DefiniteData<V>;
    }
  >;
} = {
  [Pegasus.ValueType.stringArray]: ({
    fact,
    value,
    onChange,
    options,
    error,
  }) => {
    const definiteStringOptions = options.map((option) =>
      'options' in option
        ? {
            label: option.label,
            options: option.options.map((groupOption) => ({
              label: groupOption.label,
              value: groupOption.value,
              keywords: [groupOption.label],
            })),
          }
        : {
            label: option.label,
            value: option.value,
            keywords: [option.label],
          },
    );

    const definiteStringOptionsFlat = definiteStringOptions
      .flatMap((option) => ('options' in option ? option.options : [option]))
      .filter(isTruthy);

    return (
      <div className="grow">
        <MultiSelect
          label={`${automationsDisplayName.fact[fact]} Value`}
          labelVisuallyHidden
          placeholder={`Select ${automationsDisplayName.fact[fact]}`}
          options={definiteStringOptions}
          value={value
            .map((definiteOption) =>
              definiteStringOptionsFlat.find(
                (option) => option.value === definiteOption,
              ),
            )
            .filter(isTruthy)}
          onChange={(change) => onChange(change.map((option) => option.value))}
          error={error}
          togglable
          visibleLimit={MAX_ARRAY_VALUES_VISIBLE}
        />
      </div>
    );
  },
  [Pegasus.ValueType.numberArray]: ({
    fact,
    value,
    onChange,
    options,
    error,
  }) => {
    const definiteNumberOptions = options.map((option) =>
      'options' in option
        ? {
            label: option.label,
            options: option.options.map((groupOption) => ({
              label: groupOption.label,
              value: `${groupOption.value}`,
              keywords: [groupOption.label],
            })),
          }
        : {
            label: option.label,
            value: `${option.value}`,
            keywords: [option.label],
          },
    );

    const definiteNumberOptionsFlat = definiteNumberOptions
      .flatMap((option) => ('options' in option ? option.options : [option]))
      .filter(isTruthy);

    return (
      <div className="grow">
        <MultiSelect
          label={`${automationsDisplayName.fact[fact]} Value`}
          labelVisuallyHidden
          placeholder={`Select ${automationsDisplayName.fact[fact]}`}
          options={definiteNumberOptions}
          value={value
            .map((definiteOption) =>
              definiteNumberOptionsFlat.find(
                (option) => option.value === `${definiteOption}`,
              ),
            )
            .filter(isTruthy)}
          onChange={(change) =>
            onChange(change.map((option) => Number.parseInt(option.value, 10)))
          }
          error={error}
          togglable
          visibleLimit={MAX_ARRAY_VALUES_VISIBLE}
        />
      </div>
    );
  },
};

const singleValueTypeInput: {
  [V in Exclude<Pegasus.ValueType, Pegasus.ArrayValueType>]: FC<
    ValueTypeInputProps<V>
  >;
} = {
  [Pegasus.ValueType.number]: ({ id, fact, value, onChange, error }) => (
    <NumberInput
      id={id}
      step="0.01"
      label={`${automationsDisplayName.fact[fact]} Value`}
      labelVisuallyHidden
      required
      value={value}
      onChange={(e) => onChange(e.target.valueAsNumber)}
      error={error}
    />
  ),
  [Pegasus.ValueType.boolean]: ({ fact, value, onChange, error }) => (
    <SimpleSelect
      label={`${automationsDisplayName.fact[fact]} Value`}
      labelVisuallyHidden
      value={`${value}`}
      onChange={(selected) => onChange(selected === 'true')} // convert literal back to boolean through condition
      options={[true, false].map((v) => ({ label: `${v}`, value: `${v}` }))}
      error={error}
    />
  ),
  [Pegasus.ValueType.date]: ({ id, fact, value, onChange, error }) => (
    <DateInput
      id={id}
      label={`${automationsDisplayName.fact[fact]} Value`}
      labelVisuallyHidden
      required
      value={value}
      onChange={onChange}
      error={error}
    />
  ),
};

function EventModifyIncentives() {
  const { currencyCode, currencySymbol } = useCurrency();
  const { watch, setValue, getFieldState } = useAutomationFormContext();

  const shouldModifyGiftCardIncentive = watch(
    'event.params.giftCardIncentive.shouldModify',
  );
  const incentiveType = watch('event.params.giftCardIncentive.type');
  const incentiveAmount = watch('event.params.giftCardIncentive.amount');

  return (
    <>
      <SwitchInput
        id="gift-card-incentive-offered"
        label="Modify Store Credit Incentive"
        checked={shouldModifyGiftCardIncentive}
        onChange={(e) => {
          // * handle must resolve values to match the discriminated union with additional defaults
          if (e.target.checked) {
            setValue(
              'event.params.giftCardIncentive',
              {
                shouldModify: true,
                type: CrewIncentiveTypeEnum.fixed,
                amount: 0,
              },
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
          } else {
            setValue(
              'event.params.giftCardIncentive',
              {
                shouldModify: false,
              },
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
          }
        }}
      />
      {shouldModifyGiftCardIncentive && (
        <MonetaryRateInput
          id="gift-card-incentive-amount"
          label="Store Credit Incentive Amount"
          details="The amount to offer the customer as store credit incentive."
          required
          currencyCode={currencyCode}
          currencySymbol={currencySymbol}
          value={{
            type:
              incentiveType === CrewIncentiveTypeEnum.none
                ? CrewIncentiveTypeEnum.fixed
                : toEnum(incentiveType, MonetaryType),
            amount: incentiveAmount,
          }}
          onChange={({ type, amount }) => {
            setValue('event.params.giftCardIncentive.amount', amount, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });
            setValue('event.params.giftCardIncentive.type', type, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });
          }}
          error={
            getFieldState('event.params.giftCardIncentive.amount').error
              ?.message ??
            getFieldState('event.params.giftCardIncentive.type').error?.message
          }
        />
      )}
    </>
  );
}

function EventChargeForReturnLabel() {
  const { currencyCode, currencySymbol } = useCurrency();
  const { watch, setValue, getFieldState } = useAutomationFormContext();

  const shouldApplyLabelMarkup = watch(
    'event.params.applyLabelMarkup.shouldModify',
  );
  const incentiveType = watch('event.params.applyLabelMarkup.type');
  const incentiveAmount = watch('event.params.applyLabelMarkup.amount');

  return (
    <>
      <SwitchInput
        id="gift-card-incentive-offered"
        label="Markup The Cost of the Return Label"
        checked={shouldApplyLabelMarkup}
        onChange={(e) => {
          // * handle must resolve values to match the discriminated union with additional defaults
          if (e.target.checked) {
            setValue(
              'event.params.applyLabelMarkup',
              {
                shouldModify: true,
                type: CrewIncentiveTypeEnum.fixed,
                amount: 0,
              },
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
          } else {
            setValue(
              'event.params.applyLabelMarkup',
              {
                shouldModify: false,
              },
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
          }
        }}
      />

      {shouldApplyLabelMarkup && (
        <MonetaryRateInput
          labelVisuallyHidden
          label="Label Markup Amount"
          id="label-markup-amount"
          details="The amount to markup the cost of the label."
          required
          currencyCode={currencyCode}
          currencySymbol={currencySymbol}
          value={{
            type:
              incentiveType === CrewIncentiveTypeEnum.none
                ? CrewIncentiveTypeEnum.fixed
                : toEnum(incentiveType, MonetaryType),
            amount: incentiveAmount,
          }}
          onChange={({ type, amount }) => {
            setValue('event.params.applyLabelMarkup.amount', amount, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });
            setValue('event.params.applyLabelMarkup.type', type, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });
          }}
          error={
            getFieldState('event.params.applyLabelMarkup.amount').error
              ?.message ??
            getFieldState('event.params.applyLabelMarkup.type').error?.message
          }
        />
      )}
    </>
  );
}

function EventChargeForExchangeOrderShipping() {
  const { currencyCode, currencySymbol } = useCurrency();
  const { watch, setValue, getFieldState } = useAutomationFormContext();

  const shouldApplyExchangeOrderShippingMarkup = watch(
    'event.params.applyExchangeOrderShippingMarkup.shouldModify',
  );
  const incentiveType = watch(
    'event.params.applyExchangeOrderShippingMarkup.type',
  );
  const incentiveAmount = watch(
    'event.params.applyExchangeOrderShippingMarkup.amount',
  );

  return (
    <>
      <SwitchInput
        id="gift-card-incentive-offered"
        label="Apply Additional Markup"
        checked={shouldApplyExchangeOrderShippingMarkup}
        onChange={(e) => {
          // * handle must resolve values to match the discriminated union with additional defaults
          if (e.target.checked) {
            setValue(
              'event.params.applyExchangeOrderShippingMarkup',
              {
                shouldModify: true,
                type: CrewIncentiveTypeEnum.fixed,
                amount: 0,
              },
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
          } else {
            setValue(
              'event.params.applyExchangeOrderShippingMarkup',
              {
                shouldModify: false,
              },
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
          }
        }}
      />

      {shouldApplyExchangeOrderShippingMarkup && (
        <MonetaryRateInput
          labelVisuallyHidden
          label="Label Markup Amount"
          id="exchange-order-shipping-markup-amount"
          details="The amount to markup the cost of the label."
          required
          currencyCode={currencyCode}
          currencySymbol={currencySymbol}
          value={{
            type:
              incentiveType === CrewIncentiveTypeEnum.none
                ? CrewIncentiveTypeEnum.fixed
                : toEnum(incentiveType, MonetaryType),
            amount: incentiveAmount,
          }}
          onChange={({ type, amount }) => {
            setValue(
              'event.params.applyExchangeOrderShippingMarkup.amount',
              amount,
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
            setValue(
              'event.params.applyExchangeOrderShippingMarkup.type',
              type,
              {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              },
            );
          }}
          error={
            getFieldState(
              'event.params.applyExchangeOrderShippingMarkup.amount',
            ).error?.message ??
            getFieldState('event.params.applyExchangeOrderShippingMarkup.type')
              .error?.message
          }
        />
      )}
    </>
  );
}

function EventAllowCustomerToKeepItem() {
  return (
    <Alert
      variant="info"
      heading="Eligible claims will not be automatically issued a return label or packing slip."
    />
  );
}

function EventProductRegistrationSelection() {
  const { control } = useAutomationFormContext();

  const { data: productGroups } = useProductGroups();

  const productGroupOptions = productGroups.length
    ? productGroups.map((pg) => ({
        value: `${pg.id}`,
        label: pg.name,
      }))
    : [];

  return (
    <Controller
      control={control}
      name="event.params.productRegistrationProductGroupId"
      render={({ field: { onChange, value }, fieldState }) => (
        <SimpleSelect
          options={productGroupOptions}
          label="Product Group"
          details="The product group to offer for registration."
          placeholder="Add Product Group"
          value={`${value}`}
          onChange={onChange}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

const omittableResolutionMethods = [
  CrewClaimResolutionMethodEnum.refund,
  CrewClaimResolutionMethodEnum.giftCard,
] satisfies Pegasus.OmittedResolutionMethodOptions;

function EventOmitResolutionMethods() {
  const { control } = useAutomationFormContext();
  return (
    <Controller
      control={control}
      name="event.params.omittedResolutionMethods"
      render={({ field, fieldState }) => (
        <MultiSelect
          label="Resolution Methods to Omit"
          details="The resolution methods to omit from the line item, even when it would otherwise be eligible for them."
          placeholder="Select Resolution Methods"
          options={omittableResolutionMethods.map((method) => ({
            label: crewClaimResolutionMethodEnumName[method],
            value: method,
          }))}
          value={field.value.map((method) => ({
            label: crewClaimResolutionMethodEnumName[method],
            value: method,
          }))}
          onChange={(selections) =>
            field.onChange(
              omittableResolutionMethods.filter((method) =>
                selections.some(({ value }) => value === method),
              ),
            )
          }
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

function EventAskAPolicyEnforcingQuestion() {
  const { control } = useAutomationFormContext();
  return (
    <>
      <Controller
        control={control}
        name="event.params.question"
        render={({ field, fieldState }) => (
          <TextInput
            id="question"
            label="Question"
            details="Ask a yes/no question to enforce requirements for your return policy."
            required
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.onYesMessage"
        render={({ field, fieldState }) => (
          <TextInput
            id="on-yes-message"
            label="Yes Message"
            details="Message to display when the customer answers yes, making the line item final sale and ineligible for return."
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />
    </>
  );
}

function EventOfferInstantExchanges() {
  const { control } = useAutomationFormContext();
  return (
    <>
      <Controller
        control={control}
        name="event.params.timeToReturnBeforeCharging"
        render={({ field, fieldState }) => (
          <NumberInput
            id="number-of-days"
            label="Number of days"
            details={
              <div>
                <p>
                  Number of days the customer has to return the original item
                  before being charged. If you would like to increase this time,
                  please reach out.
                </p>
              </div>
            }
            {...field}
            addon={{ insideEnd: '' }}
            error={fieldState.error?.message}
            disabled
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.isInstantExchangeRequired"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="is-required"
            label="Require Instant Exchange"
            details="When enabled, the customer will be required to complete their return as an instant exchange."
            checked={value}
            onChange={onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.instantExchangeDescriptionText"
        render={({ field, fieldState }) => (
          <TextInput
            id="instant-exchange-description-text"
            label="Detail Text"
            details="Provide additional details about the instant exchange process, this is shown when the customer selects between an Exchange or Instant Exchange. "
            {...field}
            error={fieldState.error?.message}
            defaultValue="Use a credit card to get your items sent immediately."
          />
        )}
      />
    </>
  );
}

function EventAllowClaimTypeSelection() {
  const { control } = useAutomationFormContext();
  return (
    <>
      <Controller
        control={control}
        name="event.params.claimTypeSelectionDetailText"
        render={({ field, fieldState }) => (
          <TextInput
            id="claim-type-selection-detail-text"
            label="Detail Text"
            details="The text to display to give the customer additional information when deciding between a Return or Warranty."
            placeholder='e.g. "Please select select the type of request, which best fits your needs."'
            required
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.returnSelectionButtonTitle"
        render={({ field, fieldState }) => (
          <TextInput
            id="return-selection-button-title"
            label="Return Selection Button Title"
            details="The text to display on the button for selecting a Return."
            placeholder='e.g. "Return"'
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />
    </>
  );
}

function EventCustomizeClaimLineItemInspection() {
  const { control } = useAutomationFormContext();

  const { data: customFieldOptions = [] } = useCustomFields();

  return (
    <>
      <Controller
        control={control}
        name="event.params.inspectionCustomFieldIds"
        render={({ field, fieldState }) => (
          <MultiSelect
            label="Fields to Collect"
            details="Select the fields to collect during a claim inspection, fields can be added from the Custom Fields section in the Settings."
            placeholder="Select Fields to Collect"
            options={customFieldOptions.map((customField) => ({
              label: customField.displayName,
              value: `${customField.id}`,
            }))}
            value={field.value.map((value) => ({
              label: customFieldOptions.find(
                (customField) => customField.id === value,
              )?.displayName,
              value: `${value}`,
            }))}
            onChange={(selections) =>
              field.onChange(
                selections.map(({ value }) => Number.parseInt(value, 10)),
              )
            }
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.showInspectionMediaUpload"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="show-inspection-media-upload"
            label="Show Media Upload"
            details="Show the media upload field during a claim inspection."
            checked={value}
            onChange={onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.showInspectionComment"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="show-inspection-comment-field"
            label="Show Inspection Comment Field"
            details="Show a comment field for the inspector to leave notes during the inspection."
            checked={value}
            onChange={onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.shouldCollectInspectionGrade"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="should-collect-inspection-grade"
            label="Collect Inspection Grade"
            details="Collect a grade during the inspection process."
            checked={value}
            onChange={onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.inspectionInstructions"
        render={({ field, fieldState }) => (
          <TextAreaInput
            id="inspection-instructions"
            label="Inspection Instructions"
            details="Instructions to display when inspecting a claim, such as what to look for or how to document findings."
            {...field}
            value={field.value ?? ''}
            error={fieldState.error?.message}
          />
        )}
      />
    </>
  );
}

function EventApplyClaimTags() {
  const { control } = useAutomationFormContext();
  const { data: tagOptions } = useStoreClaimTags((tags) =>
    tags.map((tag) => tag.name),
  );

  return (
    <Controller
      control={control}
      name="event.params.claimTags"
      render={({ field, fieldState }) => (
        <MultiSelect
          label="Claim Tags"
          options={(tagOptions ?? []).map((tag) => ({
            label: tag,
            value: tag,
          }))}
          details="The tags to apply to the claim."
          placeholder="Add tags"
          value={field.value.map(({ name }) => ({ label: name, value: name }))}
          onChange={(selections) =>
            field.onChange(selections.map(({ value }) => ({ name: value })))
          }
          error={fieldState.error?.message}
          creatable
        />
      )}
    />
  );
}

function EventApplyOrderTags() {
  const { control, watch } = useAutomationFormContext();
  const { data: factValues } = useFactValues();

  const tagOptions = factValues?.arbitrary['order/tags'];
  const selectedHook = watch('hook');
  const selectedAction = watch('event.params.orderTagAction');

  const orderTagActionLabel = {
    [Pegasus.OrderTagActionTarget.Replacement_Order]:
      'Replacement Order (Replacement, Exchange or Gift Card)',
    [Pegasus.OrderTagActionTarget.Original_Order]: 'Original Order',
  } satisfies Record<Pegasus.OrderTagActionTarget, string>;

  const orderTagOptions = Object.values(Pegasus.OrderTagActionTarget)
    .map((action) => ({
      value: action,
      label: orderTagActionLabel[action],
    }))
    .filter((action) =>
      selectedHook === 'Claim_Created'
        ? action.value === 'Original Order'
        : true,
    );

  return (
    <>
      <Controller
        control={control}
        name="event.params.orderTagAction"
        render={({ field, fieldState }) => (
          <SimpleSelect
            label="Which order would you like to tag?"
            details={
              selectedAction === 'Original Order'
                ? 'The original order that the claim was created from will be tagged.'
                : 'The replacement order that was created to resolve the claim will be tagged.'
            }
            placeholder="Select Order"
            options={orderTagOptions}
            onChange={field.onChange}
            value={field.value}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.tagWithReason"
        defaultValue={false}
        render={({ field: { onChange, value }, fieldState }) => (
          <SwitchInput
            id="tag-with-reason"
            label="Tag with Reason"
            details="Tag the order with the reason for the claim."
            checked={value}
            onChange={(event) => {
              onChange(event);
            }}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.orderTags"
        render={({ field, fieldState }) => (
          <MultiSelect
            label="Order Tags"
            options={(tagOptions ?? []).map((tag) => ({
              label: tag,
              value: tag,
            }))}
            details="The tags to apply."
            placeholder="Add tags"
            value={field.value.map(({ name }) => ({
              label: name,
              value: name,
            }))}
            onChange={(selections) =>
              field.onChange(selections.map(({ value }) => ({ name: value })))
            }
            error={fieldState.error?.message}
            creatable
          />
        )}
      />
    </>
  );
}

const eventParamsFields = {
  [Pegasus.EventType.modifyResolutionWindow]: EventModifyResolutionWindow,
  [Pegasus.EventType.autoFinalizeClaim]: () => null,
  [Pegasus.EventType.omitResolutionMethods]: EventOmitResolutionMethods,
  [Pegasus.EventType.askAPolicyEnforcingQuestion]:
    EventAskAPolicyEnforcingQuestion,
  [Pegasus.EventType.offerInstantExchange]: EventOfferInstantExchanges,
  [Pegasus.EventType.allowClaimTypeSelection]: EventAllowClaimTypeSelection,
  [Pegasus.EventType.collectCustomFields]: EventCollectCustomFields,
  [Pegasus.EventType.applyClaimTags]: EventApplyClaimTags,
  [Pegasus.EventType.customizeClaimFinalizationSettings]:
    EventAutoFinalizeClaimSettings,
  [Pegasus.EventType.modifyIncentives]: EventModifyIncentives,
  [Pegasus.EventType.applyFee]: EventApplyFee,
  [Pegasus.EventType.applyLineItemFee]: EventApplyLineItemFee,
  [Pegasus.EventType.chargeForReturnLabel]: EventChargeForReturnLabel,
  [Pegasus.EventType.chargeForExchangeOrderShipping]:
    EventChargeForExchangeOrderShipping,
  [Pegasus.EventType.allowCustomerToKeepItem]: EventAllowCustomerToKeepItem,
  [Pegasus.EventType.productRegistrationSelection]:
    EventProductRegistrationSelection,
  [Pegasus.EventType.returnShippingConfig]: EventReturnShippingConfig,
  [Pegasus.EventType.applyOrderTags]: EventApplyOrderTags,
  [Pegasus.EventType.customizeClaimLineItemInspection]:
    EventCustomizeClaimLineItemInspection,
  [Pegasus.EventType.quoteOrderShipment]: EventQuoteOrderShipment,
  [Pegasus.EventType.quoteOrderShipmentModify]: EventQuoteOrderShipmentModify,
  [Pegasus.EventType.quoteOrderShipmentParcel]: EventQuoteOrderShipmentParcel,
  [Pegasus.EventType.quoteOrderShipmentDeliveryEstimate]:
    EventQuoteOrderShipmentDeliveryEstimate,
  [Pegasus.EventType.offerAdvancedExchange]: EventOfferAdvancedExchanges,
  [Pegasus.EventType.orderEditing]: EventOrderEditing,
  [Pegasus.EventType.restockInventory]: EventRestockInventory,
  [Pegasus.EventType.shopNow]: EventShopNow,
  [Pegasus.EventType.registrationDiscountIncentive]: EventRegistrationDiscount,
  [Pegasus.EventType.customizeReturnShipment]: EventCustomizeReturnShipment,
  [Pegasus.EventType.merchantPaidShippingProtection]:
    EventMerchantPaidProtection,
  [Pegasus.EventType.createGorgiasTicket]: EventCreateGorgiasTicket,
  [Pegasus.EventType.rightToWithdraw]: EventRightToWithdraw,
  [Pegasus.EventType.setOrderShippingTitle]: EventSetOrderShippingTitle,
  [Pegasus.EventType.shipClaimAllowedResolutions]:
    EventShippingClaimAllowedResolutions,
  [Pegasus.EventType.shipClaimReorderLocations]:
    EventShippingClaimReorderLocations,
  [Pegasus.EventType.shipClaimSlaAlert]: EventShippingClaimSlaAlert,
  [Pegasus.EventType.shipClaimReorderShipping]:
    EventShippingClaimReorderShipping,
  [Pegasus.EventType.shipApplyOrderTags]: EventShippingClaimApplyOrderTags,
} as const satisfies Record<Pegasus.EventType, unknown>;

/** Used as the default for nearly all Hooks, to work with all inputs being controlled. */
const defaultCustomerTagExpression = () =>
  ({
    fact: Pegasus.Fact.customerTags,
    operator: Pegasus.Operator.in,
    value: [],
  }) as const satisfies Pegasus.Expression<Pegasus.Fact.customerTags>;

const defaultShipmentQuoteExpression = () =>
  ({
    fact: Pegasus.Fact.orderCountry,
    operator: Pegasus.Operator.in,
    value: [],
  }) as const satisfies Pegasus.Expression<Pegasus.Fact.orderCountry>;

const defaultShipmentQuoteModifyExpression = () =>
  ({
    fact: Pegasus.Fact.productGroupId,
    operator: Pegasus.Operator.in,
    value: [],
  }) as const satisfies Pegasus.Expression<Pegasus.Fact.productGroupId>;

/** Prefer to have a fact with an empty array for the value, just for simplicity and consistency with the underlying controlled inputs. */
const defaultExpressionForHook = {
  [Pegasus.Hook.registrationProductSelection]: () => ({
    fact: Pegasus.Fact.registrationChannel,
    operator: Pegasus.Operator.in,
    value: [],
  }),
  [Pegasus.Hook.exchangeProductSelection]: () => ({
    fact: Pegasus.Fact.productGroupId,
    operator: Pegasus.Operator.in,
    value: [],
  }),

  [Pegasus.Hook.orderLookup]: defaultCustomerTagExpression,
  [Pegasus.Hook.claimCreated]: defaultCustomerTagExpression,
  [Pegasus.Hook.afterReasonSelection]: defaultCustomerTagExpression,
  [Pegasus.Hook.beforeClaimSubmission]: defaultCustomerTagExpression,
  [Pegasus.Hook.rmaWebhook]: defaultCustomerTagExpression,
  [Pegasus.Hook.returnShipmentWebhook]: defaultCustomerTagExpression,
  [Pegasus.Hook.claimFinalized]: defaultCustomerTagExpression,
  [Pegasus.Hook.returnShipmentCreated]: defaultCustomerTagExpression,
  [Pegasus.Hook.returnShipmentQuote]: defaultCustomerTagExpression,
  [Pegasus.Hook.beforeClaimLineItemInspection]: defaultCustomerTagExpression,
  [Pegasus.Hook.orderCheckoutBegin]: defaultShipmentQuoteExpression,
  [Pegasus.Hook.claimLineItemReview]: defaultCustomerTagExpression,
  [Pegasus.Hook.orderCheckoutQuoteShipping]:
    defaultShipmentQuoteModifyExpression,
  [Pegasus.Hook.registrationCreated]: () => ({
    fact: Pegasus.Fact.registrationChannel,
    operator: Pegasus.Operator.in,
    value: [],
  }),
  [Pegasus.Hook.orderCreated]: () => ({
    fact: Pegasus.Fact.orderTags,
    operator: Pegasus.Operator.in,
    value: [],
  }),
  [Pegasus.Hook.shippingClaimLookup]: () => ({
    fact: Pegasus.Fact.orderTags,
    operator: Pegasus.Operator.in,
    value: [],
  }),
  [Pegasus.Hook.shippingClaimFinalized]: () => ({
    fact: Pegasus.Fact.orderTags,
    operator: Pegasus.Operator.in,
    value: [],
  }),
} as const satisfies {
  [H in Pegasus.Hook]: () => Pegasus.Expression<
    keyof Pegasus.FactDataForHook<H>
  >;
};

function ConditionValueField({
  i,
  condition,
}: {
  i: number;
  condition: Pegasus.Expression;
}) {
  const { control } = useAutomationFormContext();
  const { isPending, data: factValues, error } = useFactValues();

  if (isPending) {
    return <Skeleton.Rectangle height="2rem" width="8rem" />;
  }

  if (error) {
    return <Alert variant="critical" heading={error.message} />;
  }

  return (
    <Controller
      control={control}
      name={`conditions.${i}.value`}
      render={({ field, fieldState }) => {
        const fact = toEnum(condition.fact, Pegasus.Fact);
        const valueType = Pegasus.factValueType[fact];
        if (!Pegasus.isArrayValueType(valueType)) {
          // ! EXPLICIT TYPE ASSERTION // TODO resolve
          const ValueInput = singleValueTypeInput[valueType] as FC<
            ComponentProps<(typeof singleValueTypeInput)[typeof valueType]>
          >;

          return (
            <ValueInput
              id={`expressions.${i}.value`}
              fact={fact}
              // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
              value={field.value as never}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          );
        }

        if (!Pegasus.isArrayFact(fact)) {
          console.error('Unexpected Non-Array Fact', { fact });
          return <Badge variant="danger">Unexpected Fact: {fact}</Badge>;
        }

        if (
          Pegasus.isArbitraryFact(fact) &&
          Pegasus.isArbitraryValueType(valueType)
        ) {
          const ValueInput = arbitraryValueTypeInput[valueType];
          const options = factValues[Pegasus.ArrayFactType.arbitrary][fact];
          return (
            <ValueInput
              id={`expressions.${i}.value`}
              fact={fact}
              // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
              value={field.value as never}
              onChange={field.onChange}
              error={fieldState.error?.message}
              options={options}
            />
          );
        }
        if (
          Pegasus.isDefiniteFact(fact) &&
          Pegasus.isDefiniteValueType(valueType)
        ) {
          const ValueInput = definiteValueTypeInput[valueType];
          const options = factValues[Pegasus.ArrayFactType.definite][fact];
          return (
            <ValueInput
              id={`expressions.${i}.value`}
              fact={fact}
              // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
              value={field.value as never}
              onChange={field.onChange}
              error={fieldState.error?.message}
              // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
              options={options as never}
            />
          );
        }

        console.error('Unhandled Fact', { fact, valueType });
        return <Badge variant="danger">Unhandled Fact: {fact}</Badge>;
      }}
    />
  );
}

function AutomationConditionsFields() {
  const hasCheckoutExtension = useHasCheckoutFunctionality();
  const hasIntelligems = useIsIntelligemsEnabled();

  const { control, watch, setValue } = useAutomationFormContext();

  const conditionsFieldArray = useFieldArray({ control, name: 'conditions' });
  const conditionsWatch = watch('conditions');
  const eventWatch = watch('event.type');

  const eventsWithNonSelectableConditions: Pegasus.EventType[] = [
    'orderEditing',
  ];

  // refer to controlled field array: https://react-hook-form.com/docs/usefieldarray#:~:text=Controlled%20Field%20Array
  const conditions = useMemo(
    () =>
      conditionsFieldArray.fields.map((field, i) => ({
        type: 'all' as const,
        ...field,
        ...conditionsWatch[i],
      })),
    [conditionsFieldArray, conditionsWatch],
  );

  const selectedHook = watch('hook');
  const booleanExpression = watch('booleanExpression');

  const factOptions = useMemo(() => {
    let facts = new Set(Pegasus.factsForHook[selectedHook]);

    // * conditions where the facts are filtered; only omit via difference for true predicates

    // * filter for orderCheckoutQuoteShipping
    if (selectedHook === Pegasus.Hook.orderCheckoutQuoteShipping) {
      if (!hasCheckoutExtension) {
        facts = setMethods.difference(
          facts,
          new Set([Pegasus.Fact.customerTags]),
        );
      }

      // remove the intelligems fact if they don't have intelligems
      if (!hasIntelligems) {
        facts = setMethods.difference(
          facts,
          new Set([Pegasus.Fact.intelligemsTestGroupId]),
        );
      }
    }

    // * filters for WMS integrations

    // * ensure remaining fact options include any which exist as a condition; i.e. removed form the hook, or otherwise filtered out
    facts = setMethods.union(
      facts,
      new Set(conditions.map((c) => toEnum(c.fact, Pegasus.Fact))),
    );

    return Array.from(facts)
      .map((fact) => ({
        value: fact,
        label: automationsDisplayName.fact[fact],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedHook, conditions, hasCheckoutExtension, hasIntelligems]);

  return (
    <Card>
      <Card.Heading>Conditions</Card.Heading>

      <Controller
        control={control}
        name="booleanExpression"
        render={({ field, fieldState }) => (
          <RadioGroupInput
            label="Conditions"
            labelVisuallyHidden
            details="Determines if all or any of the conditions must be met for the rule to be triggered."
            id="boolean-expression"
            options={matchCondition}
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
            disabled={eventsWithNonSelectableConditions.includes(eventWatch)}
          />
        )}
      />

      {/* unconditional rule */}
      {!conditions.length && (
        <Alert
          variant="info"
          heading="Without conditions, this rule will always apply."
        />
      )}
      <ol>
        {conditions.map((condition, i) => (
          // TODO fix change of fact, operator reset isn't working as expected
          // ? possibly extract to separate component; however, that would necessitate a form context
          <li key={condition.id} className="flex flex-col">
            <Fieldset legend={`Condition ${i + 1}`}>
              <div className="flex flex-col flex-wrap justify-between gap-2 md:flex-row md:items-center">
                <div className="flex grow flex-col flex-wrap gap-2 md:flex-row md:items-center">
                  <Controller
                    control={control}
                    name={`conditions.${i}.fact`}
                    render={({ field }) => (
                      <Combobox
                        label="Fact"
                        options={factOptions}
                        value={field.value}
                        onChange={(fact) => {
                          if (!fact) return; // keep as same, deselecting is not allowed

                          setValue(`conditions.${i}.fact`, fact);
                          setValue(
                            `conditions.${i}.operator`,
                            Pegasus.operatorForFact(fact)[0],
                          );
                          setValue(
                            `conditions.${i}.value`,
                            valueTypeDefault[Pegasus.factValueType[fact]](),
                          );
                          field.onChange(fact);
                        }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`conditions.${i}.operator`}
                    render={({ field }) => (
                      <SimpleSelect
                        label="Operator"
                        labelVisuallyHidden
                        options={Pegasus.operatorForFact(condition.fact).map(
                          (operator) => {
                            const valueType =
                              Pegasus.factValueType[condition.fact];
                            return {
                              value: operator,
                              label:
                                valueTypeOperator[valueType]?.[operator] ??
                                automationsDisplayName.operator[operator],
                            };
                          },
                        )}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <ConditionValueField i={i} condition={condition} />
                </div>
                <div className="ml-auto">
                  <Action
                    accessibilityLabel={`Remove Condition ${i + 1}`}
                    icon="remove"
                    onClick={() => conditionsFieldArray.remove(i)}
                  />
                </div>
              </div>
            </Fieldset>
            {conditions.length && i < conditions.length - 1 && (
              <div className="flex flex-col items-center self-start pl-4">
                <div className="h-1.5 w-px bg-neutral-200" />
                <p className="text-sm font-semibold">
                  {booleanExpressionBadge[booleanExpression]}
                </p>
                <div className="h-1.5 w-px bg-neutral-200" />
              </div>
            )}
          </li>
        ))}
      </ol>
      <Action
        icon="add"
        disabled={!selectedHook} // there should always be a hook defined, but disabled if no hook is selected; otherwise, this could cause issues
        onClick={() =>
          conditionsFieldArray.append(defaultExpressionForHook[selectedHook]())
        }
      >
        Add Condition
      </Action>
    </Card>
  );
}

function AutomationEventTypeField() {
  const { control, setValue } = useAutomationFormContext();

  const eventDefaultParams = useEventDefaultParams();

  return (
    <Card>
      <Controller
        control={control}
        name="event.type"
        render={({ field }) => (
          <>
            {/* // TODO revamp label usage (i.e. label specified for inputs and managed via visuallyHidden prop, then have it work for combobox) */}
            <Label>What would you like to do?</Label>
            <Combobox
              label="What would you like to do?"
              options={Object.values(Pegasus.EventType)
                .filter(
                  (eventType) => !Pegasus.hiddenEvents.includes(eventType),
                )
                .sort((a, b) =>
                  automationsDisplayName.eventType[a].localeCompare(
                    automationsDisplayName.eventType[b],
                  ),
                )
                .map(
                  (eventType) =>
                    ({
                      label: (
                        <span className="text-left">
                          <p>{automationsDisplayName.eventType[eventType]}</p>
                          <p className="text-xs">
                            {eventTypeDescription[eventType]}
                          </p>
                        </span>
                      ),
                      value: eventType,
                      keywords: [
                        automationsDisplayName.eventType[eventType],
                        eventTypeDescription[eventType],
                      ],
                    }) satisfies ComboboxOption,
                )}
              placeholder="Select an Event"
              value={field.value}
              onChange={(value) => {
                if (!value) return; // keep as same, deselecting is not allowed
                field.onChange(value);
                setValue('hook', Pegasus.hooksForEvent[value][0]); // reset whatever was selected to the first valid hook for the event
                setValue('event.params', eventDefaultParams[value]()); // reset event params
                setValue('conditions', []); // reset conditions // ? could potentially filer or mark invalid rules
              }}
            />
          </>
        )}
      />
    </Card>
  );
}

function AutomationGeneralFields() {
  const {
    control,
    register,
    formState: { errors },
  } = useAutomationFormContext();

  return (
    <Card>
      <TextInput
        id="name"
        label="Name"
        details="A short name or phrase describing the rule."
        required
        {...register('name')}
        error={errors.name?.message}
      />

      <Controller
        control={control}
        name="isEnabled"
        render={({ field }) => (
          <SwitchInput
            id="is-enabled"
            label="Enabled"
            details="Enable or disable the rule."
            checked={field.value}
            onChange={field.onChange}
          />
        )}
      />
    </Card>
  );
}

/**
 * Has `children` purely for the purpose of being able to put the `children` passed to it within the `Card` component.
 */
function AutomationEventDetailFields({ children }: PropsWithChildren) {
  const { watch } = useAutomationFormContext();
  const selectedEventType = watch('event.type');
  const EventParameterFields = eventParamsFields[selectedEventType];

  return (
    <Card>
      <Card.Heading>Action</Card.Heading>
      <Fieldset
        legend={`${automationsDisplayName.eventType[selectedEventType]}`}
      >
        <div className="flex flex-col gap-4">
          <EventParameterFields />
        </div>
      </Fieldset>
      {children}
    </Card>
  );
}

function AutomationHookField() {
  const { watch, control, setValue } = useAutomationFormContext();
  const selectedEventType = watch('event.type');

  const availableHookOptions = Pegasus.hooksForEvent[selectedEventType];

  return (
    <Card>
      <Card.Heading>At</Card.Heading>
      <Controller
        // * key used to help reset the control when the event type changes, since the internal state was not updating between changes for the event type
        key={`hook-${selectedEventType}`}
        control={control}
        name="hook"
        render={({ field }) => (
          <SimpleSelect
            labelVisuallyHidden
            label="When should this rule apply?"
            options={availableHookOptions.map((hook) => ({
              label: automationsDisplayName.hook[hook],
              value: hook,
            }))}
            placeholder="Select a Hook"
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              setValue('conditions', []); // reset conditions // ? could potentially filer or mark invalid rules
            }}
          />
        )}
      />
    </Card>
  );
}

function automationToFormData(automation: Automation) {
  return {
    name: automation.name,
    hook: automation.hook,
    isEnabled: automation.isEnabled,
    event: {
      ...automation.rule.event,
      storeRuleId: automation.id, // * add `storeRuleId` explicitly; adds for pre-existing rules before it existed or any moving forward
    },
    booleanExpression: 'all' in automation.rule.conditions ? 'all' : 'any',
    conditions:
      'all' in automation.rule.conditions
        ? automation.rule.conditions.all
        : automation.rule.conditions.any,
  } satisfies AutomationFormValues;
}

function automationTemplateToFormData(automation: AutomationTemplate) {
  return {
    name: automation.name,
    hook: automation.hook,
    isEnabled: automation.isEnabled,
    event: automation.rule.event,
    booleanExpression: 'all' in automation.rule.conditions ? 'all' : 'any',
    conditions:
      'all' in automation.rule.conditions
        ? automation.rule.conditions.all
        : automation.rule.conditions.any,
  } satisfies AutomationFormValues;
}

/** Contains defaults for non-field associated value. */
function toAutomationUpsert(
  automationFormData: AutomationFormValues,
  nextPriority: number,
  {
    originalAutomation,
    automationTemplate,
  }: {
    originalAutomation?: Automation;
    automationTemplate?: AutomationTemplate;
  },
) {
  const priority = originalAutomation?.rule.priority ?? nextPriority;
  return {
    ...originalAutomation, // populate `id`, without explicitly creating the key that would then be explicitly `undefined`
    ...automationTemplate, // override with template if it exists
    name: automationFormData.name,
    isEnabled: automationFormData.isEnabled,
    hook: automationFormData.hook,
    rule: {
      priority,
      event: automationFormData.event,
      conditions:
        automationFormData.booleanExpression === 'all'
          ? { all: automationFormData.conditions }
          : { any: automationFormData.conditions },
    },
  } satisfies AutomationCreate | AutomationUpdate;
}

function AutomationExpressionValueDisplay({
  expression,
}: {
  expression: Pegasus.Expression;
}) {
  const { isPending, data: factValues, error } = useFactValues();

  if (isPending) {
    return <Skeleton.Rectangle height="1.5rem" width="6rem" />;
  }

  if (error) {
    return <Alert variant="critical" heading={error.message} />;
  }

  const valueType = Pegasus.factValueType[expression.fact];
  if (!Pegasus.isArrayValueType(valueType)) {
    return (
      // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
      <>{singleValueTypeFormatter[valueType](expression.value as never)}</>
    );
  }

  const fact = toEnum(expression.fact, Pegasus.Fact);

  if (
    Pegasus.isArbitraryFact(fact) &&
    Pegasus.isArbitraryValueType(valueType)
  ) {
    return (
      // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
      <>{arbitraryValueTypeFormatter[valueType](expression.value as never)}</>
    );
  }

  if (Pegasus.isDefiniteFact(fact) && Pegasus.isDefiniteValueType(valueType)) {
    return (
      <>
        {definiteValueTypeFormatter[valueType](
          // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
          expression.value as never,
          // ! EXPLICIT TYPE ASSERTION // TODO figure out alternative so that we can remove `as never` assertions
          factValues.definite[fact] as never,
        )}
      </>
    );
  }

  console.error('Unhandled Fact', { fact, valueType });
  return <Badge variant="danger">Unhandled Fact: {fact}</Badge>;
}

function EventDisplayBadge({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <Badge
        variant="info"
        className="text-base font-medium tracking-wide text-neutral-600 uppercase"
      >
        {text}
      </Badge>
      <Icon icon={faArrowDown} className="size-5 text-neutral-500" />
    </div>
  );
}

// designed to be similar to the `AutomationExpressionForm`
export function AutomationExpressionDisplay({
  rule,
}: {
  rule: Automation['rule'];
}) {
  const booleanExpression = 'all' in rule.conditions ? 'all' : 'any';
  const expressions =
    'all' in rule.conditions ? rule.conditions.all : rule.conditions.any;

  if (!expressions.length) {
    return <EventDisplayBadge text="ALWAYS" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <EventDisplayBadge text="IF" />
      <ol className="flex flex-col items-center gap-2 text-center">
        {expressions.map((expression, i) => {
          const valueType = Pegasus.factValueType[expression.fact];
          return (
            <li
              /* oxlint-disable-next-line react/no-array-index-key */ // TODO find a better key
              key={i}
              className="flex flex-col items-center"
            >
              <div className="flex max-w-max flex-wrap items-center justify-center gap-1.5 rounded-md border p-2">
                <Badge>{automationsDisplayName.fact[expression.fact]}</Badge>
                <Badge>
                  {valueTypeOperator[valueType]?.[expression.operator] ??
                    automationsDisplayName.operator[expression.operator]}
                </Badge>
                <AutomationExpressionValueDisplay expression={expression} />
              </div>
              {expressions.length && i < expressions.length - 1 && (
                <div className="flex flex-col items-center">
                  <div className="h-1.5 w-px bg-neutral-200" />
                  <p className="text-sm font-semibold">
                    {booleanExpressionBadge[booleanExpression]}
                  </p>
                  <div className="h-1.5 w-px bg-neutral-200" />
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <EventDisplayBadge text="THEN" />
    </div>
  );
}

export default function AutomationFormPage() {
  const storeId = useStoreId();

  const eventDefaultParams = useEventDefaultParams();

  const navigate = useNavigate();

  const templateParam = useSearchParam('template');
  const automationTemplateView = automationTemplatesWithIds.find(
    ({ id }) => id === templateParam,
  );

  const { storeRuleId } = usePathParams(
    z.object({ storeRuleId: z.coerce.number().optional() }),
  );
  const { data: automations } = useAutomations();
  const selectedAutomation = automations.find(({ id }) => id === storeRuleId);

  const values =
    // oxlint-disable-next-line  no-nested-ternary
    selectedAutomation
      ? automationToFormData(selectedAutomation)
      : automationTemplateView
        ? automationTemplateToFormData(automationTemplateView.automation)
        : undefined;

  const { mutateAsync: automationUpsert, isPending } = useAutomationUpsert();

  const methods = useAutomationForm({ values });

  const hookParam = useSearchParam('hook');
  const eventTypeParam = useSearchParam('eventType');
  const returnTo = useSearchParam('returnTo'); // * return to expected to be a pathname without anything else

  const eventType = z
    .nativeEnum(Pegasus.EventType)
    .nullable()
    .parse(eventTypeParam);

  useEffect(() => {
    // if no event type or template selected, stick with defaults; otherwise, set the selected event type as the default
    if (!eventType || automationTemplateView) return;
    methods.setValue('event.type', eventType);

    const hook = z.nativeEnum(Pegasus.Hook).nullable().parse(hookParam);
    const hookOptions: readonly Pegasus.Hook[] =
      Pegasus.hooksForEvent[eventType];
    if (hook && hookOptions.includes(hook)) {
      methods.setValue('hook', hook);
    } else {
      methods.setValue('hook', Pegasus.hooksForEvent[eventType][0]);
    }

    methods.setValue('event.params', eventDefaultParams[eventType]());
  }, [
    eventType,
    automationTemplateView,
    methods,
    hookParam,
    eventDefaultParams,
  ]);

  const isExistingOrTemplate = !!values;

  const currentEventType = selectedAutomation?.rule.event.type ?? eventType;

  const hook = methods.watch('hook');

  const nextPriority = useNextPriorityByHook(hook);

  return (
    <Page
      backAction={
        returnTo
          ? {
              accessibilityLabel: 'Back',
              to: returnTo,
            }
          : undefined
      }
      title={`${selectedAutomation ? 'Update' : 'Create'} Automation`}
      subtitle={
        <p>
          {currentEventType &&
            `${automationsDisplayName.eventType[currentEventType]}`}
        </p>
      }
    >
      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            methods
              .handleSubmit(async (automationFormData) => {
                const upsert = toAutomationUpsert(
                  automationFormData,
                  nextPriority,
                  {
                    originalAutomation: selectedAutomation,
                    automationTemplate: automationTemplateView?.automation,
                  },
                );
                const result = await automationUpsert(upsert);

                if (returnTo) {
                  navigate({
                    pathname: returnTo,
                    hash: `store-rule-${result.id}`,
                  });
                  return;
                }
                navigate({
                  pathname: `/${storeId}/settings/automations`,
                  hash: `store-rule-${result.id}`,
                });
              })(event)
              .catch((error) => console.error(error));
          }}
        >
          {automationTemplateView && (
            <Alert elevated variant="info" heading="Instructions">
              {automationTemplateView.instructions}
            </Alert>
          )}
          {!eventType && !isExistingOrTemplate && <AutomationEventTypeField />}
          <AutomationGeneralFields />
          <AutomationHookField />
          <AutomationConditionsFields />
          <AutomationEventDetailFields>
            <div className="flex flex-col gap-2 md:flex-row">
              {/* // ? probably needs some kind of default if form is directly accessed via URL without history */}
              <Action
                onClick={() => {
                  if (returnTo) {
                    navigate(returnTo);
                    return;
                  }
                  navigate(-1);
                }}
              >
                Cancel
              </Action>
              <Action variant="primary" type="submit" loading={isPending}>
                {values ? 'Save' : 'Create'}
              </Action>
            </div>
          </AutomationEventDetailFields>
        </form>
      </FormProvider>
    </Page>
  );
}
